import React, { useState } from 'react';
import SEO from '@/components/SEO';
import CaseStudySlider from '@/components/CaseStudySlider';

const CaseStudiesPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);

  const caseStudies = [
    {
      id: 1,
      emoji: '🟢',
      title: 'Developing Suriname\'s Responsible Small-Scale Gold Mining Strategy 2026–2036',
      client: 'Government of Suriname through the \'Improving Environmental Management in the Mining Sector of Suriname, with Emphasis on Artisanal and Small-Scale Gold Mining\' (EMSAGS) financed by United Nations Development Program (UNDP) and the Global Environment Facility (GEF)',
      focus: ['Policy Development', 'Sustainable Mining', 'Regulatory Compliance'],
      challenge: 'Small-scale gold mining is one of Suriname\'s most important economic activities and a major source of income for thousands of families. However, the sector also faces challenges related to environmental degradation, mercury use, land-use conflicts, occupational health and safety, limited access to finance and technology, and fragmented governance. The Government required a practical and inclusive strategy that could align stakeholders around a shared vision for responsible mining over the next decade.',
      approach: 'NDE Training & Consultancy led a participatory and evidence-based strategy development process that included: stakeholder mapping and engagement, national and international policy review, analysis of environmental, social, economic, and governance challenges, consultations with government agencies, miners, Indigenous and Tribal communities, civil society organizations, private sector actors, and development partners, facilitation of technical working sessions and validation workshops, and development of strategic objectives, priority actions, implementation arrangements, and monitoring indicators.',
      results: [
        'Developed a comprehensive national strategy and implementation framework for the period 2026–2036',
        'Facilitated dialogue among diverse stakeholder groups with often competing interests',
        'Identified priority interventions to improve environmental management, formalization, occupational health and safety, responsible mercury management, access to finance, and sector governance',
        'Established a roadmap for strengthening collaboration between government, mining communities, local populations, and development partners',
        'Produced a practical action plan with clear implementation priorities and measurable outcomes'
      ],
      impact: 'The Responsible Small-Scale Gold Mining Strategy 2026–2036 provides Suriname with a long-term framework for transitioning toward a more sustainable and inclusive mining sector. The strategy supports environmental protection, improved livelihoods, stronger governance, and greater economic resilience while recognizing the important contribution of small-scale mining to national development. It also creates a foundation for future investments, policy reforms, and international cooperation in responsible mining. NDE Training & Consultancy brought together over two decades of experience in mining, environmental management, sustainable development, and stakeholder engagement to facilitate one of Suriname\'s most important policy processes for the mining sector.',
      images: [
        '/mining-1.jpg',
        '/mining-2.jpg',
        '/mining-3.jpg',
        '/mining-4.jpg'
      ],
      hasSlider: true
    },
    {
      id: 2,
      emoji: '🟡',
      title: 'Building Community Capacity for Sustainable Development Through Project Management Training',
      client: 'TotalEnergies Exploration & Production Suriname',
      focus: ['Capacity Building', 'Community Development', 'Project Management'],
      challenge: 'Through previous stakeholder consultations, communities expressed a need for stronger project management skills to enable them to independently develop and implement initiatives that address local challenges and opportunities. Existing training efforts were often fragmented, short-term, and lacked follow-up support, limiting their long-term impact on community development.',
      approach: 'NDE Training & Consultancy implemented a structured and participatory process that included: stakeholder engagement sessions with district authorities and community members, baseline research to assess training needs, development opportunities, and local capacities, stakeholder mapping and participant identification across three districts, validation workshops to confirm findings and ensure community ownership, development of a tailored, hands-on project management curriculum, and delivery of practical training modules covering project design, budgeting, fundraising, implementation, monitoring, evaluation, and sustainability.',
      results: [
        'Conducted stakeholder engagement and baseline research across three districts',
        'Identified development priorities and project opportunities in areas such as entrepreneurship, agriculture, environmental management, tourism, youth development, and climate resilience',
        'Reached more than 120 community members through focus groups, surveys, consultations, and validation sessions',
        'Selected and mobilized over 80 participants for project management training through a transparent and inclusive process',
        'Developed a practical curriculum tailored to local realities and delivered in accessible formats, including both in-person and online learning components'
      ],
      impact: 'The project strengthened local leadership and enhanced the ability of community members to transform ideas into actionable development projects. By combining needs-based research, stakeholder participation, and practical skills training, the initiative created a foundation for more effective community-led development and increased local capacity to access funding opportunities, manage projects, and contribute to sustainable growth within their districts. NDE Training & Consultancy successfully bridged the gap between corporate social investment and community development by applying a participatory, evidence-based approach.',
      images: [
        '/totalenergies-1.png',
        '/totalenergies-2.png',
        '/totalenergies-3.jpg'
      ],
      hasSlider: true
    },
    {
      id: 3,
      emoji: '🔵',
      title: 'Developing Suriname\'s National Action Plan on Mercury Reduction in Artisanal and Small-Scale Gold Mining',
      client: 'National Institute for Environment and Development in Suriname (currently the National Environment Authority), with support from the United Nations Development Programme and the \'Improving Environmental Management in the Mining Sector of Suriname, with Emphasis on Artisanal and Small-Scale Gold Mining\' (EMSAGS) Project',
      focus: ['Environmental Policy', 'ASGM Sector', 'Mercury Reduction', 'Minamata Convention'],
      challenge: 'Artisanal and small-scale gold mining contributes approximately 40% of Suriname\'s gold production and supports the livelihoods of an estimated 10–15% of the population. However, the sector is also the country\'s largest source of mercury pollution, resulting in environmental degradation and health risks, particularly for Indigenous and Tribal communities in mining regions. At the time of the assignment, Suriname lacked a dedicated legal framework for mercury management in the sector and needed a nationally supported strategy to meet its international obligations under the Minamata Convention.',
      approach: 'NDE Training & Consultancy formed part of a multidisciplinary team responsible for developing the National Action Plan through a participatory and evidence-based process that included: comprehensive review of existing studies, policies, and sector data, baseline assessment of mercury use, environmental impacts, and health risks, consultations with government agencies, mining stakeholders, civil society organizations, Indigenous and Tribal representatives, and development partners, community engagement sessions in mining-affected areas, facilitation of validation workshops and stakeholder review processes, and development of reduction targets, implementation strategies, monitoring mechanisms, and policy recommendations.',
      results: [
        'Delivered Suriname\'s first National Action Plan for mercury reduction in the ASGM sector',
        'Established a national target to reduce mercury use in small-scale gold mining by 30% over a ten-year period',
        'Developed a comprehensive implementation framework covering legal and institutional reform, mercury reduction measures and technology adoption, public health interventions and biomonitoring, and awareness and education programs',
        'Identified practical actions to eliminate harmful mining practices, increase retort use, improve monitoring systems, and strengthen government capacity',
        'Achieved broad stakeholder validation and national ownership of the proposed strategy'
      ],
      impact: 'The National Action Plan provides Suriname with a clear roadmap for reducing mercury pollution while maintaining the economic importance of the small-scale gold mining sector. The plan strengthens the country\'s ability to protect public health, improve environmental management, support sustainable mining practices, and fulfill its commitments under the Minamata Convention. It also establishes the foundation for future investments, policy reforms, capacity-building initiatives, and coordinated action among government, industry, communities, and development partners. NDE Training & Consultancy successfully translated complex environmental, health, and mining-sector challenges into a practical and implementable national policy framework.',
      images: [
        '/minamata-1.png',
        '/minamata-2.png',
        '/minamata-3.png'
      ],
      hasSlider: true
    },
    {
      id: 4,
      emoji: '🟣',
      title: 'Evaluating Community-Led Climate Resilience and Sustainable Development in Suriname\'s Hinterland',
      client: 'Conservation International Suriname',
      focus: ['Project Evaluation', 'Climate Resilience', 'Community Development'],
      challenge: 'The project operated in remote hinterland communities facing environmental pressures, governance challenges, limited economic opportunities, and the need for greater inclusion of women and youth in local development processes. Conservation International Suriname required an independent evaluation to determine the effectiveness, impact, sustainability, and lessons learned from a three-year, multi-partner project involving communities, CSOs, government agencies, and development partners.',
      approach: 'NDE Training & Consultancy designed and executed a comprehensive evaluation process that included: extensive review of project documentation and performance reports, stakeholder mapping and analysis, field missions to Brownsweg and Matawai, interviews with community leaders, beneficiaries, CSOs, government representatives, consultants, and project partners, data analysis against project objectives and performance indicators, and assessment of organizational performance, sustainability, and future opportunities for improvement.',
      results: [
        'Assessed a €943,726 European Union-funded project that directly benefited over 600 community members and strengthened 14 local civil society organizations',
        'Confirmed successful delivery of major project outputs, including community forest monitoring programs, training of Amazon Conservation Rangers, capacity-building programs for women, youth, and community organizations',
        'Evaluated establishment of a craft market, sanitary facilities, agricultural plots, and a rice mill to support sustainable livelihoods',
        'Identified strengths, implementation challenges, lessons learned, and recommendations for future community-based conservation and development initiatives'
      ],
      impact: 'The evaluation demonstrated that the LACH project significantly strengthened local leadership, enhanced community participation in environmental management, empowered women-led organizations, improved sustainable livelihood opportunities, and increased local capacity for forest monitoring and conservation. The findings provided Conservation International Suriname with actionable recommendations to strengthen future programming, improve sustainability mechanisms, and build upon the project\'s successes in supporting climate resilience and community development in Suriname\'s hinterland. NDE Training & Consultancy combined technical evaluation expertise, stakeholder engagement, field-based data collection, and strategic analysis to deliver an evidence-based assessment.',
      images: [
        '/lach-1.png',
        '/lach-2.png',
        '/lach-3.png'
      ],
      hasSlider: true
    },
    {
      id: 5,
      emoji: '🟤',
      title: 'Evaluating and Strengthening a National Green Business Partnership',
      client: 'Suriname Conservation Foundation (SCF)',
      focus: ['Partnership Evaluation', 'Sustainable Business', 'Strategic Development'],
      challenge: 'After more than a decade of operation, SCF sought to assess the effectiveness of its Green Partnership Program, identify opportunities for improvement, strengthen collaboration among partner organizations, and define a clear pathway for the future of the partnership. Key areas of concern included communication, partner engagement, resource sharing, governance structure, and long-term sustainability.',
      approach: 'NDE Training & Consultancy was engaged as an independent facilitator and evaluator. The assignment included: reviewing relevant program documentation, designing and administering stakeholder surveys, conducting interviews with partner organizations, analyzing data and identifying key trends, facilitating a multi-stakeholder validation workshop, and developing recommendations and strategic next steps for the partnership.',
      results: [
        'Achieved broad stakeholder participation through interviews, surveys, and a validation workshop',
        'Confirmed strong overall support for continuation of the partnership',
        'Identified practical recommendations to improve communication, governance, transparency, and collaboration',
        'Facilitated consensus on priority actions, including enhanced reporting, regular meetings, integration of sustainability goals, and development of a renewed partnership framework'
      ],
      impact: 'The evaluation provided SCF and its partner organizations with a clear roadmap for strengthening the Green Partnership Program and enhancing its contribution to sustainable development in Suriname. The process renewed stakeholder commitment, improved strategic alignment, and laid the foundation for future collaborative environmental initiatives and long-term partnership growth. Through a participatory and evidence-based approach, NDE Training & Consultancy successfully facilitated dialogue among leading private sector organizations, translated stakeholder insights into actionable recommendations, and supported the development of a stronger framework for environmental stewardship and sustainable business collaboration in Suriname.',
      images: [
        '/ngo-1.png',
        '/ngo-2.png'
      ],
      hasSlider: true
    }
  ];

  return (
    <div data-testid="case-studies-page">
      <SEO 
        title="Case Studies"
        description="Explore NDE Training & Consultancy's portfolio of successful projects across Suriname. From responsible mining strategy to community development, see our measurable impact in sustainable development."
        keywords="case studies Suriname, responsible mining projects, community development Suriname, environmental projects, project evaluation, capacity building success stories"
        url="https://ndebusiness.com/case-studies"
      />
      <section 
        className="section-spacing text-white relative bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(/case-studies-header.jpg)',
          minHeight: '305px'
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="content-container relative z-10">
          <h1 className="font-rubik text-4xl md:text-5xl font-bold text-center mb-4" data-testid="case-studies-title">
            CASE STUDIES
          </h1>
          <p className="font-rubik text-2xl text-center mb-2">🌍 Our Impact</p>
          <p className="font-rubik text-xl font-semibold text-center mb-2">Delivering Results That Matter</p>
          <p className="font-roboto text-base text-center max-w-3xl mx-auto">
            From national policy to community-level transformation, our work creates measurable, lasting impact across Suriname and the Guiana Shield.
          </p>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="content-container">
          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <div
                key={study.id}
                className={`${index % 2 === 0 ? 'bg-white' : 'bg-[#f5f5f5]'} p-8 rounded-lg`}
                data-testid={`case-study-${study.id}`}
              >
                <h2 className="font-rubik text-2xl md:text-3xl font-bold mb-6 text-[#646464]">
                  {study.emoji} {study.title}
                </h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Column 1: Image or Slider */}
                  <div className="lg:col-span-1">
                    {study.hasSlider ? (
                      <CaseStudySlider 
                        images={study.images} 
                        onImageClick={setLightboxImage}
                      />
                    ) : (
                      <div 
                        className="relative w-full h-64 lg:h-full min-h-[300px] bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setLightboxImage(study.image)}
                      >
                        <img 
                          src={study.image} 
                          alt={study.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity">
                          <div className="bg-white/90 rounded-full p-3">
                            <svg className="w-8 h-8 text-[#aaae10]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Columns 2-3: Content */}
                  <div className="lg:col-span-2">
                    <div className="space-y-4">
                      <div>
                        <p className="font-roboto text-sm text-[#646464]">
                          <strong>Client:</strong> {study.client}
                        </p>
                        <p className="font-roboto text-sm text-[#646464]">
                          <strong>Focus:</strong> {study.focus.join(' | ')}
                        </p>
                      </div>

                      <div>
                        <h3 className="font-rubik text-lg font-bold text-[#aaae10] mb-2">The Challenge</h3>
                        <p className="font-roboto text-base leading-relaxed">{study.challenge}</p>
                      </div>

                      <div>
                        <h3 className="font-rubik text-lg font-bold text-[#aaae10] mb-2">Our Approach</h3>
                        <p className="font-roboto text-base leading-relaxed">{study.approach}</p>
                      </div>

                      <div>
                        <h3 className="font-rubik text-lg font-bold text-[#aaae10] mb-2">Key Results</h3>
                        <ul className="space-y-2">
                          {study.results.map((result, idx) => (
                            <li key={idx} className="font-roboto text-base flex items-start">
                              <span className="text-[#aaae10] mr-2">✔</span>
                              <span>{result}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h3 className="font-rubik text-lg font-bold text-[#aaae10] mb-2">Impact</h3>
                        <p className="font-roboto text-base leading-relaxed">{study.impact}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white text-5xl font-light hover:text-[#aaae10] transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxImage(null);
            }}
            aria-label="Close fullscreen image"
          >
            ×
          </button>
          <img 
            src={lightboxImage} 
            alt="Full screen view"
            className="max-w-full max-h-full object-contain"
            style={{ maxHeight: '90vh', maxWidth: '90vw' }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <section className="section-spacing bg-[#646464] text-white">
        <div className="content-container text-center">
          <h2 className="font-rubik text-3xl font-bold mb-4">
            PARTNER WITH US ON YOUR NEXT PROJECT
          </h2>
          <p className="font-roboto text-lg mb-6 max-w-2xl mx-auto">
            Let's work together to create sustainable solutions that deliver measurable
            results and lasting impact for your organization and community.
          </p>
          <a href="/contact" className="btn-primary" data-testid="case-studies-cta-button">
            Start a Conversation
          </a>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
